import type { TFunction } from 'i18next';
import type {
  MeasureArea,
  MeasureStep,
  Proposal,
  ProposalError,
  ShareKind,
  WorkEnvironmentSurvey,
} from '../../api/workEnvironmentTypes';
import { WorkEnvironmentSurveyStatus } from '../../api/workEnvironmentTypes';
import type { FieldError } from './assessmentView';
import { dayAndMonth, daysLeft } from './surveyProcessView';

/*
 * The limits are checked again in the API, which is the source of truth.
 * They are here so the form can count down while the person types.
 */
export const MAX_TITLE_LENGTH = 80;

const MEASURE_STATUSES: WorkEnvironmentSurveyStatus[] = [
  WorkEnvironmentSurveyStatus.ACTION_PROPOSAL_OPEN,
  WorkEnvironmentSurveyStatus.ACTION_VOTING_OPEN,
];

/** Surveys with an HMS risk follow the new measures. Older ones keep the old dashboard. */
export const showsMeasuresPage = (survey: Pick<WorkEnvironmentSurvey, 'status' | 'riskCalculatedAt'>) =>
  Boolean(survey.riskCalculatedAt) && MEASURE_STATUSES.includes(survey.status);

/** The areas that need measures first, then the others */
export const areasByNeed = (areas: MeasureArea[]) => ({
  needing: areas.filter((area) => area.needsMeasures),
  others: areas.filter((area) => !area.needsMeasures),
});

export const proposalsIn = <T extends Pick<Proposal, 'factorKey'>>(proposals: T[], areaKey: string | null) =>
  areaKey ? proposals.filter((proposal) => proposal.factorKey === areaKey) : proposals;

/** «Teammedlem (anonym)», or who proposed it when it was the leader */
export const proposalOrigin = (proposal: Pick<Proposal, 'fromLeader' | 'createdBy'>, t: TFunction) =>
  proposal.fromLeader
    ? t('workEnvironmentMeasures.origin.leader', { email: proposal.createdBy ?? '' })
    : t('workEnvironmentMeasures.origin.team');

export const PROPOSAL_FIELDS: Record<ProposalError, string> = {
  area: 'proposal-area',
  title: 'proposal-title',
  titleLength: 'proposal-title',
  reasonLength: 'proposal-reason',
};

export const proposalFieldErrors = (errors: ProposalError[], t: TFunction): FieldError[] =>
  errors.map((error) => ({
    fieldId: PROPOSAL_FIELDS[error],
    message: t(`workEnvironmentMeasures.errors.${error}`, { max: MAX_TITLE_LENGTH }),
  }));

export const charactersLeft = (title: string) => MAX_TITLE_LENGTH - title.length;

/** Choosing a picked proposal takes it back. Beyond the limit nothing changes, and the page says why. */
export const togglePick = (picks: number[], id: number, max: number) =>
  picks.includes(id) ? picks.filter((pick) => pick !== id) : picks.length < max ? [...picks, id] : picks;

/** The link the team uses for both proposing and pointing out measures */
export const teamLink = (origin: string, uuid: string) => `${origin}/work-environment-actions/${uuid}`;

/** The proposals grouped by area, in the order of the areas, leaving out areas without proposals */
export const proposalsByArea = <T extends Pick<Proposal, 'factorKey'>>(areas: MeasureArea[], proposals: T[]) =>
  areas
    .map((area) => ({ area, proposals: proposalsIn(proposals, area.key) }))
    .filter((group) => group.proposals.length > 0);

/** «Frist for forslag om 5 dager», or what the step waits for when the team has no deadline to meet */
export const measuresTag = (step: MeasureStep, deadline: string | null, t: TFunction, now = new Date()) => {
  if (step === 'review') return t('workEnvironmentMeasures.tag.review');
  return step !== 'plan' && deadline
    ? t(`workEnvironmentMeasures.tag.${step}`, { count: daysLeft(deadline, now) })
    : t('workEnvironmentMeasures.tag.plan');
};

/*
 * The API is the source of truth for the order of the proposals. The page needs the same rule to
 * know whether to show the rank and the picks.
 */
export const ranksByHighlights = (step: MeasureStep) => step === 'highlight' || step === 'plan';

const SHARE_OF_STEP: Record<MeasureStep, ShareKind | null> = {
  propose: 'PROPOSALS',
  review: null,
  highlight: 'HIGHLIGHTS',
  plan: null,
};

/** The link the team gets for the step. While the leader reviews, the team has nothing to open. */
export const shareKindOf = (step: MeasureStep) => SHARE_OF_STEP[step];

/** Starting the highlighting is what the leader has left to do once the proposals are reviewed */
export const closeButtonVariant = (step: MeasureStep) => (step === 'review' ? 'primary' : 'secondary');

/**
 * The team was told how long they have. Closing the step before that breaks what they were
 * told, so the team leader sees how many days early it is.
 */
export const closeEarlyText = (
  step: Exclude<MeasureStep, 'plan'>,
  deadline: string | null,
  t: TFunction,
  language: string,
  now = new Date(),
) => {
  const days = deadline ? daysLeft(deadline, now) : 0;
  return deadline && days > 0
    ? t(`workEnvironmentMeasures.next.${step}.early`, { date: dayAndMonth(deadline, language), count: days })
    : null;
};

const CLOSED_CODES = ['proposalsClosed', 'highlightsClosed'];

const errorCodeOf = (error: unknown) => (error as { response?: { data?: { code?: string } } }).response?.data?.code;

/** The deadline may close the step while the team leader does, and then there is nothing to close */
export const closeErrorKey = (error: unknown) =>
  CLOSED_CODES.includes(errorCodeOf(error) ?? '') ? 'alreadyClosed' : 'failed';

/** The deadline may pass while a team member writes a proposal */
export const proposeErrorKey = (error: unknown) => (errorCodeOf(error) === 'proposalsClosed' ? 'closed' : 'failed');
